/**
 * Ações de conversa: criar, atribuir, transferir, encerrar e reabrir.
 */
import { useCallback, useRef, type Dispatch, type SetStateAction } from 'react';
import { supabase } from '../../../lib/supabase';
import { chatRepository } from '../../../services/repositories/ChatRepository';
import { createModuleLogger } from '../../../utils/logger';
import { generateJidVariants, normalizePhoneForChat } from '../../../utils/phoneUtils';
import { ChatConversation } from './types';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useAssignment } from '../../../contexts/assignment/AssignmentContext';
import { notify } from '../../../utils/toast';
import { emitConversationMetaChanged } from '../utils/partykitSocket';
import {
    conversationIdentityMatch,
    findConversation,
    scopeConversationQuery,
    shouldQueryOrphanConversation,
} from '../lib/conversationScope';
import { conversationDedupeKey } from '../../../services/repositories/helpers/ChatConversationMapper';
import { fetchOrphanConversationsByRemoteJid } from './chatSync.helpers';

const log = createModuleLogger('[ChatConvActions]');
type ConversationUpdate = { status?: string | null };

const phoneFromJid = (jid: string) => jid.replace('@s.whatsapp.net', '').replace(/\D/g, '');

export function useChatConversationActions(
    userIdProp: string | null,
    connectionId: string | null,
    setConversations: Dispatch<SetStateAction<ChatConversation[]>>,
    checkAndAutoUnfavorite?: (conversationId: string) => Promise<boolean>,
    conversations: ChatConversation[] = [],
) {
    const { currentOrganization } = useOrganization();
    const { user } = useAuth();
    const { assignUser } = useAssignment();
    const conversationsRef = useRef(conversations);
    conversationsRef.current = conversations;

    const userId = user?.id || userIdProp;
    const orgId = currentOrganization?.id;

    const emitMeta = useCallback((
        remoteJid: string,
        status: 'closed' | 'active',
        connectionId?: string | null,
    ) => {
        if (!orgId) return;
        const phone = remoteJid.startsWith('ig:') ? undefined : phoneFromJid(remoteJid);
        emitConversationMetaChanged({
            organizationId: orgId,
            phone: phone || undefined,
            remoteJid,
            connectionId: connectionId || undefined,
            status,
        });
    }, [orgId]);

    const patchLocal = useCallback((
        conversationId: string,
        patch: Partial<ChatConversation>,
        connectionId?: string | null,
    ) => {
        const match = findConversation(conversationsRef.current, conversationId, connectionId);
        const scopeId = connectionId ?? match?.connectionId ?? null;
        setConversations((prev) => prev.map((c) => (
            conversationIdentityMatch(c, conversationId, scopeId) ? { ...c, ...patch } : c
        )));
    }, [setConversations]);

    /**
     * Escopo de mutação: connection da própria conversa.
     * Sem fallback no seletor WA — senão .eq(connection_id) erra linhas com null.
     */
    const mutationConnectionScope = useCallback((conversationId: string) => {
        const match = findConversation(conversationsRef.current, conversationId, connectionId);
        return match?.connectionId ?? null;
    }, [connectionId]);

    const updateConversation = useCallback(async (
        conversationId: string,
        updates: ConversationUpdate,
        localUpdate: (conv: ChatConversation) => ChatConversation,
        successMessage: string,
    ): Promise<boolean> => {
        if (!userId) {
            log.error('Faltando dados para atualizar conversa');
            return false;
        }
        try {
            const scopeConnectionId = mutationConnectionScope(conversationId);
            let query = supabase.from('chat_conversations')
                .update(updates)
                .in('remote_jid', generateJidVariants(conversationId));
            query = scopeConversationQuery(query, scopeConnectionId, orgId);
            const { error } = await query;
            if (error) {
                log.error('Erro ao atualizar conversa:', error);
                return false;
            }
            setConversations((prev) => prev.map((c) => (
                conversationIdentityMatch(c, conversationId, scopeConnectionId)
                    ? localUpdate(c)
                    : c
            )));
            emitMeta(
                conversationId,
                updates.status === 'closed' ? 'closed' : 'active',
                scopeConnectionId,
            );
            log.log(successMessage);
            return true;
        } catch (error) {
            log.error('Erro ao atualizar conversa:', error);
            return false;
        }
    }, [userId, mutationConnectionScope, orgId, setConversations, emitMeta]);

    const getConversationDbId = useCallback(async (jid: string): Promise<string | null> => {
        const match = findConversation(conversationsRef.current, jid, connectionId);
        const scopeConnectionId = match?.connectionId ?? connectionId ?? null;
        const variants = generateJidVariants(jid);

        let query = supabase.from('chat_conversations')
            .select('id')
            .in('remote_jid', variants)
            .order('last_message_time', { ascending: false })
            .limit(1);
        query = scopeConversationQuery(query, scopeConnectionId, orgId);
        const { data } = await query.maybeSingle();
        if (data?.id) return data.id;

        // Conversa na UI com connectionId, mas DB ainda null — atende a linha órfã.
        if (!shouldQueryOrphanConversation(false, scopeConnectionId, orgId)) return null;
        const orphan = await fetchOrphanConversationsByRemoteJid(jid, orgId!, {
            select: 'id',
            orderBy: 'last_message_time',
            limit: 1,
        });
        return orphan.data?.[0]?.id || null;
    }, [orgId, connectionId]);

    /** assign / transfer / remove — mesmo fluxo, só muda o userId alvo */
    const mutateAssignment = useCallback(async (
        conversationId: string,
        assignedUserId: string | null,
        options?: { successLog?: string; failureLog?: string; onSuccess?: () => Promise<void> },
    ): Promise<boolean> => {
        const dbId = await getConversationDbId(conversationId);
        if (!dbId) {
            log.error('Conversa não encontrada no banco');
            return false;
        }
        const success = await assignUser({
            entityType: 'conversation',
            entityId: dbId,
            userId: assignedUserId,
            phone: conversationId.startsWith('ig:') ? undefined : phoneFromJid(conversationId),
            silent: true,
        });
        if (!success) {
            if (options?.failureLog) log.error(options.failureLog);
            return false;
        }
        const scopeId = findConversation(conversationsRef.current, conversationId, connectionId)?.connectionId
            ?? connectionId
            ?? null;
        patchLocal(conversationId, { assignedTo: assignedUserId || undefined }, scopeId);
        if (options?.onSuccess) await options.onSuccess();
        if (options?.successLog) log.log(options.successLog);
        return true;
    }, [getConversationDbId, assignUser, patchLocal, connectionId]);

    const startNewConversation = useCallback(async (number: string, name: string) => {
        const jid = normalizePhoneForChat(number);
        let resolvedJid = jid;
        if (connectionId) {
            const { data: existingConv } = await supabase.from('chat_conversations')
                .select('id, remote_jid')
                .in('remote_jid', generateJidVariants(jid))
                .eq('connection_id', connectionId)
                .order('last_message_time', { ascending: false })
                .limit(1)
                .maybeSingle();
            if (existingConv?.remote_jid) resolvedJid = existingConv.remote_jid;
        }

        const newConv: ChatConversation = {
            id: resolvedJid,
            name: name || number,
            unreadCount: 0,
            messages: [],
            lastMessageTime: new Date(),
            assignedTo: userId || undefined,
            status: 'active',
            connectionId: connectionId || undefined,
            platform: resolvedJid.startsWith('ig:') ? 'instagram' : 'whatsapp',
        };

        setConversations((prev) => {
            const key = conversationDedupeKey(newConv);
            const existing = prev.find((c) => conversationDedupeKey(c) === key);
            if (!existing) return [newConv, ...prev];
            const updated = { ...existing, name: name || existing.name };
            return [updated, ...prev.filter((c) => conversationDedupeKey(c) !== key)];
        });

        if (userId && connectionId) {
            await chatRepository.upsertConversation(userId, connectionId, newConv, orgId);
        }
        emitMeta(resolvedJid, 'active', connectionId);
        return resolvedJid;
    }, [userId, connectionId, orgId, setConversations, emitMeta]);

    const assignConversation = useCallback(async (conversationId: string) => {
        if (!userId) return false;
        return mutateAssignment(conversationId, userId, { successLog: '✅ Conversa atribuída com sucesso' });
    }, [userId, mutateAssignment]);

    const transferConversation = useCallback(async (conversationId: string, targetUserId: string) => {
        notify.info('🔄 Transferindo conversa...');
        return mutateAssignment(conversationId, targetUserId, {
            failureLog: 'Falha ao transferir conversa',
            onSuccess: async () => {
                if (!checkAndAutoUnfavorite) return;
                try { await checkAndAutoUnfavorite(conversationId); }
                catch (error) { console.error('[Transfer] Erro ao desfavoritar:', error); }
            },
        });
    }, [mutateAssignment, checkAndAutoUnfavorite]);

    const removeAssignment = useCallback(async (conversationId: string) => (
        mutateAssignment(conversationId, null, { successLog: '✅ Atribuição removida com sucesso' })
    ), [mutateAssignment]);

    const closeConversation = useCallback(async (conversationId: string) => (
        updateConversation(conversationId, { status: 'closed' }, (c) => ({ ...c, status: 'closed' as const }), '✅ Conversa encerrada com sucesso')
    ), [updateConversation]);

    const reopenConversation = useCallback(async (conversationId: string) => (
        updateConversation(conversationId, { status: null }, (c) => ({ ...c, status: undefined }), '✅ Conversa reaberta com sucesso')
    ), [updateConversation]);

    return {
        startNewConversation,
        assignConversation,
        transferConversation,
        removeAssignment,
        closeConversation,
        reopenConversation,
    };
}
